import { parseCurrency } from "../invoiceUtils";

const cents = (value) => Math.round(Number(value || 0) * 100);
const tenThousandths = (value) => Math.round(Number(value || 0) * 10000);
const invoiceCurrency = (invoice) => invoice.currency || String(invoice.amount || "EUR").match(/\b[A-Z]{3}\b/)?.[0] || "EUR";
const compact = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const partyKey = (value) => compact(value).replace(/(llc|ltd|gmbh|inc|shpk|company|studio|labs)$/g, "");

const possibleBankFee = (payment, recordAmount, referenceMatch, partyMatch) => {
  const difference = Number((Number(payment || 0) - Number(recordAmount || 0)).toFixed(4));
  const maximum = Math.max(1, Math.min(10, Number(recordAmount || 0) * 0.05));
  const bankFeeAmount = (referenceMatch || partyMatch) && cents(difference) > 0 && cents(difference) <= cents(maximum)
    ? difference
    : 0;
  return { bankFeeAmount, feeAdjusted: bankFeeAmount > 0 };
};

const parseDate = (value) => {
  const text = String(value || "").trim();
  const bkt = text.match(/^(\d{2})-([A-Z]{3})-(\d{2})$/i);
  if (bkt) {
    const month = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"].indexOf(bkt[2].toUpperCase());
    if (month >= 0) return Date.UTC(2000 + Number(bkt[3]), month, Number(bkt[1]));
  }
  const parsed = Date.parse(text);
  return Number.isFinite(parsed) ? parsed : null;
};

export const invoiceTotal = (invoice) => parseCurrency(invoice?.amount);

export const confirmedAllocations = (transactions, exceptTransactionId = "") => transactions.reduce((totals, transaction) => {
  if (transaction.id === exceptTransactionId || !transaction.reconciliation?.confirmedAt) return totals;
  transaction.reconciliation.allocations?.forEach(({ invoiceId, amount }) => {
    totals[invoiceId] = (totals[invoiceId] || 0) + Number(amount || 0);
  });
  return totals;
}, {});

export const invoiceBalance = (invoice, transactions = [], exceptTransactionId = "") => {
  const paid = confirmedAllocations(transactions, exceptTransactionId)[invoice.id] || 0;
  return Math.max(0, Number((invoiceTotal(invoice) - paid).toFixed(2)));
};

const scoreInvoice = (transaction, invoice, balance) => {
  const amount = Math.abs(transaction.signedAmount);
  const sourceText = compact(`${transaction.reference} ${transaction.description} ${transaction.counterparty}`);
  const number = compact(invoice.invoiceNumber || invoice.id);
  const party = partyKey(transaction.counterparty || transaction.description);
  const client = partyKey(invoice.client);
  const exactAmount = cents(amount) === cents(balance);
  const referenceMatch = number.length >= 4 && sourceText.includes(number);
  const partyMatch = party.length >= 4 && client.length >= 4 && (party.includes(client) || client.includes(party));
  const { bankFeeAmount, feeAdjusted } = possibleBankFee(amount, balance, referenceMatch, partyMatch);
  const transactionDate = parseDate(transaction.bookedAt);
  const invoiceDate = parseDate(invoice.issuedDate);
  const dayDistance = transactionDate !== null && invoiceDate !== null ? Math.abs(transactionDate - invoiceDate) / 86400000 : null;
  const dateScore = dayDistance === null ? 0 : dayDistance <= 45 ? 12 : dayDistance <= 120 ? 5 : 0;
  const score = (referenceMatch ? 90 : 0) + (exactAmount ? 55 : 0) + (feeAdjusted ? 46 : 0) + (partyMatch ? 28 : 0) + dateScore;
  const reasons = [referenceMatch && "invoice reference", exactAmount && "exact amount", feeAdjusted && "possible bank fee", partyMatch && "client name", dateScore && "date proximity"].filter(Boolean);
  return { score, reasons, exactAmount, referenceMatch, partyMatch, bankFeeAmount, feeAdjusted };
};

const evidenceCurrency = (item) => item.accountingEvidence?.currency || "EUR";
const evidenceAmount = (item) => Math.abs(Number(item.accountingEvidence?.grossAmount || 0));
const evidenceText = (item) => `${item.accountingEvidence?.reference || ""} ${item.accountingEvidence?.supplier || ""} ${item.originalFilename || ""}`;

const scoreEvidence = (transaction, item) => {
  const payment = Math.abs(Number(transaction.signedAmount || 0));
  const record = item.accountingEvidence || {};
  const sourceText = compact(`${transaction.reference} ${transaction.description} ${transaction.counterparty}`);
  const reference = compact(record.reference);
  const supplier = partyKey(record.supplier);
  const counterparty = partyKey(transaction.counterparty || transaction.description);
  const exactAmount = cents(payment) === cents(evidenceAmount(item));
  const referenceMatch = reference.length >= 4 && sourceText.includes(reference);
  const partyMatch = supplier.length >= 4 && counterparty.length >= 4 && (supplier.includes(counterparty) || counterparty.includes(supplier));
  const transactionDate = parseDate(transaction.bookedAt);
  const documentDate = parseDate(record.documentDate);
  const dayDistance = transactionDate !== null && documentDate !== null ? Math.abs(transactionDate - documentDate) / 86400000 : null;
  const dateScore = dayDistance === null ? 0 : dayDistance <= 14 ? 18 : dayDistance <= 45 ? 10 : dayDistance <= 120 ? 3 : 0;
  const score = (referenceMatch ? 90 : 0) + (exactAmount ? 60 : 0) + (partyMatch ? 32 : 0) + dateScore;
  const reasons = [referenceMatch && "document reference", exactAmount && "exact amount", partyMatch && "supplier name", dateScore && "date proximity"].filter(Boolean);
  return { score, reasons, exactAmount, referenceMatch, partyMatch, dayDistance };
};

const purchaseReference = (purchase) => purchase.documentNumber || purchase.externalReference || purchase.id;
const purchaseOutstanding = (purchase) => {
  if (purchase?.balanceAmount !== undefined && purchase?.balanceAmount !== null) {
    return Math.max(0, Number(Number(purchase.balanceAmount || 0).toFixed(4)));
  }
  return Math.max(0, Number((Number(purchase?.grossAmount || 0) - Number(purchase?.paidAmount || 0)).toFixed(4)));
};

const scorePurchase = (transaction, purchase, balance) => {
  const payment = Math.abs(Number(transaction.signedAmount || 0));
  const sourceText = compact(`${transaction.reference} ${transaction.description} ${transaction.counterparty}`);
  const reference = compact(purchaseReference(purchase));
  const vendor = partyKey(purchase.vendorName);
  const counterparty = partyKey(transaction.counterparty || transaction.description);
  const exactAmount = tenThousandths(payment) === tenThousandths(balance);
  const referenceMatch = reference.length >= 4 && sourceText.includes(reference);
  const partyMatch = vendor.length >= 4 && counterparty.length >= 4 && (vendor.includes(counterparty) || counterparty.includes(vendor));
  const { bankFeeAmount, feeAdjusted } = possibleBankFee(payment, balance, referenceMatch, partyMatch);
  const transactionDate = parseDate(transaction.bookedAt);
  const documentDate = parseDate(purchase.issuedDate);
  const dayDistance = transactionDate !== null && documentDate !== null ? Math.abs(transactionDate - documentDate) / 86400000 : null;
  const dateScore = dayDistance === null ? 0 : dayDistance <= 14 ? 18 : dayDistance <= 45 ? 10 : dayDistance <= 120 ? 3 : 0;
  const score = (referenceMatch ? 90 : 0) + (exactAmount ? 60 : 0) + (feeAdjusted ? 46 : 0) + (partyMatch ? 32 : 0) + dateScore;
  const reasons = [referenceMatch && "document reference", exactAmount && "exact amount", feeAdjusted && "possible bank fee", partyMatch && "vendor name", dateScore && "date proximity"].filter(Boolean);
  return { score, reasons, exactAmount, referenceMatch, partyMatch, dayDistance, bankFeeAmount, feeAdjusted };
};

const findExactCombination = (candidates, amount, normalize = cents) => {
  const limited = candidates.filter((candidate) => candidate.balance > 0).slice(0, 8);
  for (let left = 0; left < limited.length; left += 1) {
    for (let right = left + 1; right < limited.length; right += 1) {
      if (normalize(limited[left].balance + limited[right].balance) === normalize(amount)) return [limited[left], limited[right]];
      for (let third = right + 1; third < limited.length; third += 1) {
        if (normalize(limited[left].balance + limited[right].balance + limited[third].balance) === normalize(amount)) return [limited[left], limited[right], limited[third]];
      }
    }
  }
  return [];
};

export const classifyAllocation = (transaction, invoiceIds, invoices, transactions = [], suggestedBankFeeAmount = 0) => {
  const selected = invoiceIds.map((id) => invoices.find((invoice) => invoice.id === id)).filter(Boolean);
  const payment = Math.abs(Number(transaction.signedAmount || 0));
  const bankFeeAmount = Math.min(payment, Math.max(0, Number(Number(suggestedBankFeeAmount || 0).toFixed(4))));
  const settlementAmount = Number((payment - bankFeeAmount).toFixed(4));
  const balances = selected.map((invoice) => ({ invoice, balance: invoiceBalance(invoice, transactions, transaction.id) }));
  const totalBalance = balances.reduce((sum, item) => sum + item.balance, 0);
  const difference = Number((settlementAmount - totalBalance).toFixed(2));
  const allocations = [];
  let remaining = settlementAmount;
  balances.forEach(({ invoice, balance }) => {
    const amount = Math.min(balance, remaining);
    if (amount > 0) allocations.push({ invoiceId: invoice.id, amount: Number(amount.toFixed(2)) });
    remaining = Number((remaining - amount).toFixed(2));
  });
  let classification = "No match";
  if (selected.length > 1) classification = "Combined payment";
  else if (selected.length === 1 && cents(settlementAmount) === cents(totalBalance)) classification = bankFeeAmount > 0 ? "Invoice + bank fee" : "Exact match";
  else if (selected.length === 1 && settlementAmount < totalBalance) classification = "Partial payment";
  else if (selected.length === 1 && settlementAmount > totalBalance) classification = "Overpayment";
  return { classification, invoiceIds: selected.map((invoice) => invoice.id), allocations, payment, settlementAmount, bankFeeAmount, totalBalance: Number(totalBalance.toFixed(2)), difference };
};

export const suggestReconciliation = (transaction, invoices, transactions = []) => {
  if (!transaction || transaction.signedAmount <= 0) return { direction: "outgoing", candidates: [], recommended: null };
  const eligible = invoices.filter((invoice) => invoice.issuerId === transaction.businessId && !invoice.archived && invoice.status !== "Draft" && invoiceCurrency(invoice) === transaction.currency && invoiceBalance(invoice, transactions, transaction.id) > 0);
  const candidates = eligible.map((invoice) => {
    const balance = invoiceBalance(invoice, transactions, transaction.id);
    return { invoice, balance, ...scoreInvoice(transaction, invoice, balance) };
  }).sort((left, right) => right.score - left.score || left.invoice.dueDate?.localeCompare(right.invoice.dueDate || "") || 0);
  const payment = Math.abs(transaction.signedAmount);
  const exactCombination = findExactCombination(candidates, payment);
  const best = candidates[0];
  const recommendedIds = best?.score >= 28 ? [best.invoice.id] : exactCombination.map((candidate) => candidate.invoice.id);
  const recommendedFee = recommendedIds.length === 1 && best?.invoice.id === recommendedIds[0] ? best.bankFeeAmount : 0;
  const recommended = recommendedIds.length ? classifyAllocation(transaction, recommendedIds, invoices, transactions, recommendedFee) : null;
  return { direction: "incoming", candidates, recommended };
};

export const suggestOutgoingEvidence = (transaction, intakeItems = [], transactions = []) => {
  if (!transaction || transaction.signedAmount >= 0) return { direction: "incoming", candidates: [], recommended: null };
  const linkedEvidenceIds = new Set(transactions
    .filter((candidate) => candidate.id !== transaction.id && candidate.evidenceMatch?.confirmedAt)
    .map((candidate) => candidate.evidenceMatch.intakeId));
  const eligible = intakeItems.filter((item) => item.businessId === transaction.businessId
    && item.status === "Accepted"
    && ["Purchase invoice", "Receipt"].includes(item.documentType)
    && evidenceAmount(item) > 0
    && evidenceCurrency(item) === transaction.currency
    && !linkedEvidenceIds.has(item.id));
  const candidates = eligible.map((item) => ({
    item,
    amount: evidenceAmount(item),
    currency: evidenceCurrency(item),
    label: evidenceText(item),
    ...scoreEvidence(transaction, item),
  })).sort((left, right) => right.score - left.score || String(right.item.accountingEvidence?.documentDate || "").localeCompare(String(left.item.accountingEvidence?.documentDate || "")));
  const best = candidates[0];
  const exactCandidates = candidates.filter((candidate) => candidate.exactAmount);
  const recommended = best && best.score >= 60 && (best.referenceMatch || best.partyMatch || exactCandidates.length === 1) ? best : null;
  return { direction: "outgoing", candidates, recommended };
};

export const classifyPurchaseAllocation = (transaction, purchaseDocumentIds, purchases, suggestedBankFeeAmount = 0) => {
  const selected = purchaseDocumentIds.map((id) => purchases.find((purchase) => purchase.id === id)).filter(Boolean);
  const payment = Math.abs(Number(transaction?.signedAmount || 0));
  const bankFeeAmount = Math.min(payment, Math.max(0, Number(Number(suggestedBankFeeAmount || 0).toFixed(4))));
  const settlementAmount = Number((payment - bankFeeAmount).toFixed(4));
  const balances = selected.map((purchase) => ({ purchase, balance: purchaseOutstanding(purchase) }));
  const totalBalance = Number(balances.reduce((sum, item) => sum + item.balance, 0).toFixed(4));
  const difference = Number((settlementAmount - totalBalance).toFixed(4));
  const allocations = [];
  let remaining = settlementAmount;
  balances.forEach(({ purchase, balance }) => {
    const amount = Math.min(balance, remaining);
    if (amount > 0) allocations.push({ purchaseDocumentId: purchase.id, amount: Number(amount.toFixed(4)) });
    remaining = Number((remaining - amount).toFixed(4));
  });
  let classification = "No match";
  if (selected.length > 1) classification = "Combined payment";
  else if (selected.length === 1 && tenThousandths(settlementAmount) === tenThousandths(totalBalance)) classification = bankFeeAmount > 0 ? "Purchase + bank fee" : "Exact match";
  else if (selected.length === 1 && settlementAmount < totalBalance) classification = "Partial payment";
  else if (selected.length === 1 && settlementAmount > totalBalance) classification = "Overpayment";
  return {
    classification,
    purchaseDocumentIds: selected.map((purchase) => purchase.id),
    allocations,
    payment,
    settlementAmount,
    bankFeeAmount,
    totalBalance,
    difference,
  };
};

export const suggestOutgoingPurchases = (transaction, purchases = []) => {
  if (!transaction || Number(transaction.signedAmount || 0) >= 0) return { direction: "incoming", candidates: [], recommended: null };
  const eligible = purchases.filter((purchase) => purchase.businessId === transaction.businessId
    && !purchase.archived
    && String(purchase.reviewStatus || "").toLowerCase() === "reviewed"
    && !["void", "paid", "overpaid"].includes(String(purchase.paymentStatus || "").toLowerCase())
    && purchase.currency === transaction.currency
    && purchaseOutstanding(purchase) > 0);
  const candidates = eligible.map((purchase) => {
    const balance = purchaseOutstanding(purchase);
    return { purchase, balance, ...scorePurchase(transaction, purchase, balance) };
  }).sort((left, right) => right.score - left.score || String(right.purchase.issuedDate || "").localeCompare(String(left.purchase.issuedDate || "")));
  const payment = Math.abs(Number(transaction.signedAmount || 0));
  const exactCombination = findExactCombination(candidates, payment, tenThousandths);
  const best = candidates[0];
  const recommendedIds = best?.score >= 32
    ? [best.purchase.id]
    : exactCombination.map((candidate) => candidate.purchase.id);
  const recommendedFee = recommendedIds.length === 1 && best?.purchase.id === recommendedIds[0] ? best.bankFeeAmount : 0;
  const recommended = recommendedIds.length ? classifyPurchaseAllocation(transaction, recommendedIds, purchases, recommendedFee) : null;
  return { direction: "outgoing", candidates, recommended };
};

export const reconciliationStatusLabel = (transaction) => transaction.reconciliation?.bankFeeAmount > 0
  ? Number(transaction.signedAmount || 0) < 0 ? "Purchase + bank fee" : "Invoice + bank fee"
  : transaction.reconciliation?.classification || transaction.reconciliationStatus || "Unmatched";
